import { apiClient, apiClientPublic } from "services/api-client";

// vo-api-vendor answers a rejected action with HTTP 200 + { success: false, code, error }.
// Surface those as errors so no caller can report success for something the server refused.
const requireSuccess = (response) => {
  if (response?.data?.success === false) {
    const error = new Error(response.data.error || 'Request rejected');
    error.code = response.data.code;
    error.details = response.data;
    error.response = response;
    throw error;
  }
  return response;
};

/**
 * Assessment API Service
 * Manages vendor security assessments, submission workflows, and trust scoring
 */

// ========== ASSESSMENT CRUD OPERATIONS ==========

/**
 * Get all assessments with optional filters
 */
export const getAssessments = async (tenant, filters = {}) => {
  let query = `/vendor/assessment?companyKey=${tenant}`;

  if (filters.status) {
    query += `&status=${filters.status}`;
  }
  if (filters.vendorId) {
    query += `&vendorId=${filters.vendorId}`;
  }
  if (filters.frameworkId) {
    query += `&frameworkId=${filters.frameworkId}`;
  }
  if (filters.page && filters.perPage) {
    query += `&page=${filters.page}&limit=${filters.perPage}`;
  }

  return await apiClient.get(query);
};

/**
 * Get a single assessment by ID
 */
export const getAssessmentById = async (id) => {
  return await apiClient.get(`/vendor/assessment/${id}`);
};

/**
 * Generate a new assessment for a vendor
 */
export const generateAssessment = async (assessmentData) => {
  return await apiClient.post('/vendor/assessment', assessmentData);
};

/**
 * Send initial invitation email to vendor
 */
export const sendInvitation = async (id) => {
  return requireSuccess(await apiClient.post(`/vendor/assessment/${id}/send-invitation`));
};

/**
 * Resend invitation email to vendor
 */
export const resendInvitation = async (id) => {
  return requireSuccess(await apiClient.post(`/vendor/assessment/${id}/resend-invitation`));
};

/**
 * The vendor's questionnaire link (same link the invitation email carries) — only while the
 * assessment awaits the vendor response; otherwise rejects with code VENDOR_LINK_NOT_ACTIVE.
 */
/**
 * Users an executive-approval request can be sent to: the current tenant's own active users.
 */
export const getEligibleExecutives = async (tenant) => {
  return requireSuccess(await apiClient.get(`/vendor/executive-approvals/eligible-executives?companyKey=${tenant}`));
};

export const getVendorLink = async (id, tenant) => {
  return requireSuccess(await apiClient.get(`/vendor/assessment/${id}/vendor-link?companyKey=${tenant}`));
};

// ========== VENDOR SUBMISSION OPERATIONS (Token-based) ==========

/**
 * Get assessment by vendor access token (public, no auth)
 * Returns both assessment metadata and control responses in a single call
 */
export const getAssessmentByToken = async (token) => {
  return requireSuccess(await apiClientPublic.get(`/vendor-submission/${token}/assessment`));
};

/**
 * Update vendor response to control question
 */
export const updateVendorResponse = async (token, controlResponseId, data) => {
  const body = {};
  if (data.maturityLevel !== undefined) {
    body.vendorMaturityLevel = data.maturityLevel;
  }
  if (data.notes !== undefined) {
    body.notes = data.notes;
  }
  return requireSuccess(await apiClientPublic.put(`/vendor-submission/${token}/control-response/${controlResponseId}`, body));
};

/**
 * Upload evidence file for a control
 */
export const uploadEvidence = async (token, controlResponseId, formData) => {
  return requireSuccess(await apiClientPublic.post(`/vendor-submission/${token}/control-response/${controlResponseId}/evidence`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }));
};

/**
 * Submit completed vendor assessment
 */
export const submitVendorAssessment = async (token) => {
  return requireSuccess(await apiClientPublic.post(`/vendor-submission/${token}/submit`));
};

// ========== INTERNAL REVIEW OPERATIONS ==========

/**
 * Get control responses with details for gap analysis
 */
export const getControlResponses = async (assessmentId) => {
  return await apiClient.get(`/vendor/assessment/${assessmentId}/control-responses`);
};

/**
 * Get trust score for an assessment
 */
export const getTrustScore = async (assessmentId) => {
  return await apiClient.get(`/vendor/assessment/${assessmentId}/trust-score`);
};

/**
 * Unlock controls for vendor remediation
 */
export const unlockControls = async (assessmentId, controlIds, notes) => {
  return await apiClient.put(`/vendor/assessment/${assessmentId}/unlock`, {
    controlIds,
    notes
  });
};

/**
 * Approve an assessment
 */
export const approveAssessment = async (assessmentId, notes) => {
  return await apiClient.put(`/vendor/assessment/${assessmentId}/approve`, {
    notes
  });
};

/**
 * Request executive approval for high-risk assessment
 */
export const requestExecutiveApproval = async (assessmentId, executiveUserId, justification) => {
  return await apiClient.post(`/vendor/assessment/${assessmentId}/request-approval`, {
    executiveUserId,
    justification
  });
};

// ========== EXECUTIVE APPROVAL OPERATIONS ==========

/**
 * Approve an assessment as executive
 */
export const executiveApproveAssessment = async (assessmentId, justification) => {
  return await apiClient.post(`/vendor/executive-approvals/${assessmentId}/approve`, { justification });
};

/**
 * Reject an assessment as executive
 */
export const executiveRejectAssessment = async (assessmentId, justification) => {
  return await apiClient.post(`/vendor/executive-approvals/${assessmentId}/reject`, { justification });
};

/**
 * Pending executive approvals assigned to the logged-in user
 */
export const getMyPendingExecutiveApprovals = async (tenant) => {
  return await apiClient.get(`/vendor/executive-approvals/pending?companyKey=${tenant}`);
};

/**
 * Get executive approval history for an assessment
 */
export const getExecutiveApprovalHistory = async (assessmentId) => {
  return await apiClient.get(`/vendor/executive-approvals/assessment/${assessmentId}`);
};

/**
 * Get evidence file download URL
 */
export const getEvidenceDownloadUrl = async (fileId) => {
  return await apiClient.get(`/vendor/evidence/${fileId}/download`);
};

/**
 * Get activity timeline for an assessment
 */
export const getAssessmentTimeline = async (assessmentId) => {
  return await apiClient.get(`/vendor/assessment/${assessmentId}/timeline`);
};
